import * as React from 'react';
import * as PropTypes from 'prop-types';
import stringsResx from '../../Share.resx';
import * as StringHelper from '@ms/odsp-utilities/lib/string/StringHelper';
import './ShareLinkDetails.scss';
import {
  ISharingLink, ISharingLinkSettings, ISharingStore,
  SharingLinkKind, SharingAudience, SharingRole
} from '../../../interfaces/SharingInterfaces';
import { ShareLinkParticipants } from '../ShareLinkParticipants/ShareLinkParticipants';
import PeoplePicker from '../../PeoplePicker/PeoplePicker';
import * as PeoplePickerHelper from '../../../utilities/PeoplePickerHelper';
import { IPerson } from '@ms/odsp-datasources/lib/PeoplePicker';
import { autobind } from 'office-ui-fabric-react/lib/Utilities';
import { ConfirmationDialog, ConfirmationCode } from '../ShareLink/ConfirmationDialog/ConfirmationDialog';
import { ExpirationDatePicker } from '../../ModifyPermissions/ExpirationDatePicker/ExpirationDatePicker';
import { ChangePermission } from '../ShareLink/ChangePermission/ChangePermission';
import { getExpirationErrorCode, ExpirationErrorCode, getExpirationDatePickerErrorMessage } from '../../../utilities/ShareHelper';
import { IconButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import { PreventDownload } from '../../ModifyPermissions/PreventDownload/PreventDownload';
import { PasswordField } from '../../ModifyPermissions/PasswordField/PasswordField';
import { ShareLink, ISharingLinkProps } from '../ShareLink/ShareLink';
export interface ISharingLinkDetailsProps extends ISharingLinkProps {
  participants: IPerson[];
  participant?: IPerson;
  hasSettingsChange: boolean;
  selectedPermissions: ISharingLinkSettings;
  getLinkSettings: () => ISharingLinkSettings;
  updateLinkSettings: (inviteeToRemove: IPerson) => void;
  onRoleChange: (value: SharingRole) => void;
  onExpirationChange: (expiration: Date) => void;
  onPasswordChange: (newPassword: string) => void;
  onPeoplePickerChange: (match: Array<IPerson>, pickerInputs: Array<IPerson>) => void;
  onCancel: () => void;
  onRemoveLink?: () => void;
}

export interface ISharingLinkDetailsState {
  link?: ISharingLink;
  peoplePickerError: JSX.Element;
  showConfirmationDialog: boolean;
  confirmationCode: ConfirmationCode;
  expirationErrorCode: ExpirationErrorCode;
  hasSettingsChange: boolean;
  hasPeopleChange: boolean;
  linkRemovedText: string;
  participants?: IPerson[];
  participant?: IPerson;
  selectedPermissions: ISharingLinkSettings;
  participantRemovedText: string;
}

export class ShareLinkDetails extends React.Component<ISharingLinkDetailsProps, ISharingLinkDetailsState> {
  private _store: ISharingStore;
  private _link: ISharingLink;

  static contextTypes = {
    sharingStore: PropTypes.object.isRequired
  };

  constructor(props: ISharingLinkDetailsProps, context: any) {
    super(props);

    this._store = context.sharingStore;
    this._link = this.props.link;

    this.state = {
      expirationErrorCode: ExpirationErrorCode.none,
      hasSettingsChange: this.props.hasSettingsChange || false,
      hasPeopleChange: false,
      showConfirmationDialog: false,
      confirmationCode: ConfirmationCode.none,
      participants: this.props.participants,
      linkRemovedText: '',
      participantRemovedText: '',
      selectedPermissions: this.props.selectedPermissions,
      peoplePickerError: PeoplePickerHelper.renderPickerError({
        selectedItems: { ...this.props.participants },
        sharingLinkKind: SharingLinkKind.direct,
        canAddExternalPrincipal: this.props.sharingInformation.canAddExternalPrincipal,
        sharingInformation: this.props.sharingInformation,
        viewPolicyTipCallback: this._store.getPolicyTipInformation
      })
    };
  }

  public render(): React.ReactElement<{}> {
    return (
      <div className={ 'od-ShareLinkDetails--div' }>
        { this._renderDetailsPanelHeader() }
        { this._renderDetailsPanelContent() }
        { this._renderDetailsPanelActions() }
      </div>
    );
  }

  @autobind
  private _renderDetailsPanelContent() {
    return (
      <div className='od-ShareLinkDetails--content'>
        { this._renderShareLink() }
        <div className='od-ShareLinkDetails--sectionHeader'
          data-is-focusable={ true }
          aria-label={ stringsResx.modifyPermissionsHeader }
        >
          { stringsResx.modifyPermissionsHeader }
        </div>
        <ul className='od-ShareLinkDetails--changeSettings' aria-labelledby={ stringsResx.modifyPermissionsHeader } >
          { this._renderChangePermission() }
          { this._renderExpirationDatePicker() }
          { this._renderPasswordField() }
          { this._renderPreventDownloadToggle() }
        </ul>
        { this.props.selectedPermissions.audience !== SharingAudience.specificPeopleFlex && this.state.participants.length > 0 &&
          <div className='od-ShareLinkDetails--sectionHeader'
            data-is-focusable={ true }
            aria-label={ stringsResx.notFlexibleLinkPeoplePickerHeader }
          >
            { stringsResx.notFlexibleLinkPeoplePickerHeader }
          </div>
        }
        { this.props.selectedPermissions.audience === SharingAudience.specificPeopleFlex &&
          <div className='od-ShareLinkDetails--sectionHeader'
            data-is-focusable={ true }
            aria-label={ stringsResx.flexibleLinkPeoplePickerHeader }
          >
            { stringsResx.flexibleLinkPeoplePickerHeader }
          </div>
        }
        { this._renderPeoplePicker() }
        { this._renderParticipantsList() }
        <div className='od-Share-screenReaderOnly' aria-live='assertive' role='alert' aria-label={ this.state.participantRemovedText }>
          { this.state.participantRemovedText }
        </div>
        { this.state.showConfirmationDialog && this._renderConfirmationDialog() }
      </div>
    );
  }

  @autobind
  private _renderShareLink() {
    return (
      <ShareLink
        link={ this._link }
        companyName={ this.props.companyName }
        clientId={ this.props.clientId }
        mode={ this.props.mode }
        sharingInformation={ this.props.sharingInformation }
        groupsMemberCount={ this.props.groupsMemberCount }
        linkRecipients={ this.props.linkRecipients }
        enableLinkSettings={ this.props.enableLinkSettings }
        showMoreOptions={ false }
        showPermissionsList={ this.props.showPermissionsList }
        onRemoveLink={ this.props.onRemoveLink }
      />
    );
  }

  @autobind
  private _renderDetailsPanelHeader(): JSX.Element {
    return (
      <div className='od-ShareLinkDetails--header'>
        <IconButton
          data-is-focusable={ true }
          className='od-ShareLink-actionButton'
          iconProps={ { iconName: 'Back' } }
          onClick={ this._onBack }
          ariaLabel={ stringsResx.backButtonLabel }
        />
        <span>{ stringsResx.manageExistingAccessLabel }</span>
      </div>
    );
  }

  @autobind
  private _renderDetailsPanelActions(): JSX.Element {
    const disableButton = !(this.state.hasPeopleChange || this.state.hasSettingsChange) || this.state.expirationErrorCode !== ExpirationErrorCode.none;
    return (
      <div className='od-ShareLinkDetails--actions'>
        { <DefaultButton
          primary={ true }
          onClick={ this._onSave }
          disabled={ disableButton }
        >
          { stringsResx.saveButtonText }
        </DefaultButton>
        }
        <DefaultButton
          onClick={ this.props.showPermissionsList }
          disabled={ disableButton }
        >
          { stringsResx.discardButtonText }
        </DefaultButton>
      </div>
    );
  }

  @autobind
  private _renderParticipantsList() {
    if (this.state.participants.length > 0) {
      return (
        <ShareLinkParticipants
          link={ this._link }
          showDetailsPanel={ true }
          participants={ this.state.participants }
          updateLinkSettings={ this.props.updateLinkSettings }
          onRemoveParticipant={ this._onRemoveParticipant } />
      );
    } else {
      // If there are no people on a PSL, show empty state.
      if (this._link.audience === SharingAudience.specificPeopleFlex) {
        return (
          <div className='od-ShareLinkDetails--noParticipants'>
            { stringsResx.noLinkParticipantsPSL }
          </div>
        );
      }
    }
  }

  private _renderPeoplePicker() {
    const props = this.props;
    const state = this.state;
    const externalRecipientWarning = PeoplePickerHelper.getOversharingExternalsWarning(state.participants);
    const groupRecipientWarning = PeoplePickerHelper.getOversharingGroupsWarning(state.participants, this.props.groupsMemberCount);
    if (this.props.selectedPermissions.audience === SharingAudience.specificPeopleFlex) {
      return (
        <PeoplePicker
          defaultSelectedItems={ [] }
          onChange={ this._onPeoplePickerChange }
          oversharingExternalsWarning={ externalRecipientWarning }
          oversharingGroupsWarning={ groupRecipientWarning }
          pickerSettings={ props.sharingInformation.peoplePickerSettings }
          error={ state.peoplePickerError }
          sharingInformation={ props.sharingInformation }
          sharingLinkKind={ props.link.sharingLinkKind }
          clientId={ this.props.clientId }
          inputTextPlaceholder={ stringsResx.peoplePickerPlaceholderSpecific }
          onResolvedSuggestions={ null }
        />
      );
    }
  }

  @autobind
  private _onPeoplePickerChange(pickerInputs: Array<IPerson>): void {
    let peoplePickerError = PeoplePickerHelper.renderPickerError({
      selectedItems: this.state.participants,
      sharingLinkKind: this._link.sharingLinkKind,
      canAddExternalPrincipal: this.props.sharingInformation.canAddExternalPrincipal,
      sharingInformation: this.props.sharingInformation,
      viewPolicyTipCallback: this._store.getPolicyTipInformation,
      permissionsMap: this._store.getPermissionsMap()
    });

    let peopleToAdd = pickerInputs[0];
    let match = [];
    if (peopleToAdd.isExternal === true) {
      match = this.state.participants.filter(participant => participant.email === peopleToAdd.email);
    } else {
      match = this.state.participants.filter(participant => participant.userId === peopleToAdd.userId);
    }

    this.setState({
      participants: match.length === 0 ? this.state.participants.concat(pickerInputs) : this.state.participants,
      hasPeopleChange: match.length === 0,
      peoplePickerError: peoplePickerError,
    });

    this.props.onPeoplePickerChange(match, pickerInputs);
  }

  @autobind
  private _onRemoveParticipant(participantToRemove: IPerson, currentParticipants: Array<IPerson>): void {
    this.setState({
      participants: currentParticipants,
      participantRemovedText: StringHelper.format(stringsResx.removedPersonText, participantToRemove.name)
    });

    this.props.updateLinkSettings(participantToRemove);
  }

  private _renderChangePermission() {
    return (
      <li className='od-ShareLinkDetails--setting'>
        <div className='od-ShareLinkDetails--changePermissions'>
          <ChangePermission
            selectedPermissions={ this.props.getLinkSettings() }
            onRoleChange={ this._onRoleChange }
            clientId={ this.props.clientId }
            appmap={ this.props.sharingInformation.item.appmap }
          />
        </div>
      </li>
    );
  }

  @autobind
  private _onRoleChange(value): void {
    this.setState({
      hasSettingsChange: true
    });

    this.props.onRoleChange(value);
  }

  /**
     * Render ExpirationDatePicker component if audience is 'Anonymous',
     * otherwise don't render anything.
     */
  private _renderExpirationDatePicker() {
    const selectedPermissions = this.state.selectedPermissions;

    if (selectedPermissions.audience === SharingAudience.anyone) {
      return (
        <li className='od-ShareLinkDetails--setting'>
          <div className='od-ShareLinkDetails--datePicker'>
            <ExpirationDatePicker
              clientId={ this.props.clientId }
              onSelectDate={ this._onSelectDate }
              value={ selectedPermissions.expiration }
              expiryRestriction={ this.props.sharingInformation.anonymousLinkExpirationRestrictionDays }
            />
          </div>
          <span className='od-ShareLinkDetails--expirationError' role='alert' aria-live='assertive'>
            { this._renderExpirationDatePickerErrorMessage() }
          </span>
        </li>
      );
    }
  }

  private _renderExpirationDatePickerErrorMessage() {
    const expirationErrorCode = this.state.expirationErrorCode;
    return getExpirationDatePickerErrorMessage(expirationErrorCode, this.props.sharingInformation.anonymousLinkExpirationRestrictionDays);
  }

  private _renderPasswordField() {
    const selectedPermissions = this.state.selectedPermissions;

    if (selectedPermissions.hasPassword) {
      let lastModifiedByLabel = '';
      const lastModifiedBy = this._link.passwordLastModifiedBy;
      if (lastModifiedBy) {
        if (lastModifiedBy.primaryText) {
          lastModifiedByLabel = lastModifiedBy.primaryText;
        } else if (lastModifiedBy.email) {
          lastModifiedByLabel = lastModifiedBy.email;
        } else {
          lastModifiedByLabel = '';
        }
      }
      return (
        <li className='od-ShareLinkDetails--setting'>
          <div className='od-ShareLink-detailsPanel-passwordFieldWrapper'>
            <div className='od-ShareLink-detailsPanel-passwordField'>
              <PasswordField
                onPasswordChange={ this._onPasswordChange }
                detailsPane={ true }
                lastModifiedBy={ lastModifiedByLabel }
              />
            </div>
          </div>
        </li>
      );
    }
  }

  @autobind
  private _onPasswordChange(newPassword: string): void {
    this.setState(() => {
      return {
        ...this.state,
        hasSettingsChange: newPassword.length > 0,
      };
    });

    this.props.onPasswordChange(newPassword);
  }

  private _renderPreventDownloadToggle() {
    const selectedPermissions = this.state.selectedPermissions;
    const preventDownload = selectedPermissions.preventDownload;

    if (preventDownload) {
      return (
        <li className='od-ShareLinkDetails--setting'>
          <div className='od-ShareLinkDetails--preventDownloadToggle'>
            <PreventDownload
              isToggled={ preventDownload }
              disablePreventDownload={ true }
              detailsPane={ true }
              clientId={ this.props.clientId }
              mode={ this.props.mode }
            />
          </div>
        </li>
      );
    }
  }

  private _renderConfirmationDialog() {
    const { confirmationCode, participants, participant } = this.state;

    return (
      <ConfirmationDialog
        confirmationCode={ confirmationCode }
        participants={ participants }
        participant={ participant }
        onApply={ this._onConfirmationSave }
        onCancel={ this.state.confirmationCode === ConfirmationCode.back ?
          this._onCancel
          :
          () => {
            this.setState({
              showConfirmationDialog: false,
              confirmationCode: ConfirmationCode.none,
            });
          } }
        onDiscard={ this.props.showPermissionsList }
      />
    );
  }

  @autobind
  private _onConfirmationSave(): void {
    this.props.updateLinkSettings(null);

    this.setState(
      {
        ...this.state,
        showConfirmationDialog: false,
        confirmationCode: ConfirmationCode.none,
        hasSettingsChange: false,
        hasPeopleChange: false,
      }
    );
  }

  @autobind
  private _onCancel(): void {
    this.setState({
      ...this.state,
      showConfirmationDialog: false,
      confirmationCode: ConfirmationCode.none,
    });

    this.props.onCancel();
  }

  @autobind
  private _onSave(): void {
    if (this.state.hasSettingsChange) {
      this.setState({
        ...this.state,
        confirmationCode: ConfirmationCode.linkSettings,
        showConfirmationDialog: true,
      });
    } else if (this.state.hasPeopleChange) {
      this.setState({
        ...this.state,
        hasPeopleChange: false,
      });

      this.props.updateLinkSettings(null);
    }
  }

  @autobind
  private _onBack() {
    if (this.state.hasPeopleChange || this.state.hasSettingsChange) {
      this.setState({
        ...this.state,
        showConfirmationDialog: true,
        confirmationCode: ConfirmationCode.back
      });
    } else {
      this.props.onCancel();
    }
  }

  @autobind
  private _onSelectDate(date: Date): void {
    const restrictionDays = this.props.sharingInformation.anonymousLinkExpirationRestrictionDays;

    this.setState({
      ...this.state,
      expirationErrorCode: getExpirationErrorCode(date, restrictionDays)
    }, () => {
      this._updateExpirationErrorCode(getExpirationErrorCode(date, restrictionDays));
    });

    this._onExpirationChange(date);
  }

  private _onExpirationChange(date: Date): void {
    this.setState(() => {
      return {
        ...this.state,
        selectedPermissions: {
          ...this.state.selectedPermissions,
          expiration: date
        },
        hasSettingsChange: date !== this.state.selectedPermissions.expiration ? true : this.state.hasSettingsChange,
      };
    });

    this.props.onExpirationChange(date);
  }

  @autobind
  private _updateExpirationErrorCode(code: ExpirationErrorCode) {
    if (code !== this.state.expirationErrorCode) {
      this.setState({
        ...this.state,
        expirationErrorCode: code
      });
    }
  }
}
